import * as THREE from 'three';
import type {
  DesktopQualityProfile,
  DesktopQualityRecommendation,
  DesktopQualityTier,
  FrameTimeStatistics,
  PerformanceBottleneck,
  PerformanceDiagnosticsBridge,
  PerformanceGovernorOptions,
  PerformanceGovernorSnapshot,
  PhysicsPerformanceSample,
  QualityChangeEvent,
  QualityChangeListener,
  RecordFrameOptions,
  RendererPerformanceSnapshot,
  ScenePerformanceSnapshot,
} from './types';

const QUALITY_ORDER: readonly DesktopQualityTier[] = ['safe', 'balanced', 'high', 'ultra'];

export const DESKTOP_QUALITY_PROFILES: Readonly<
  Record<DesktopQualityTier, DesktopQualityProfile>
> = Object.freeze({
  safe: Object.freeze({
    tier: 'safe',
    label: 'Safe desktop',
    maxDevicePixelRatio: 1.25,
    postProcessingScale: 0.72,
    bloomStrengthScale: 0.58,
    primaryShadowMapSize: 1024,
    reflections: 'reduced-update-rate',
  }),
  balanced: Object.freeze({
    tier: 'balanced',
    label: 'Balanced desktop',
    maxDevicePixelRatio: 1.5,
    postProcessingScale: 0.82,
    bloomStrengthScale: 0.72,
    primaryShadowMapSize: 1024,
    reflections: 'full',
  }),
  high: Object.freeze({
    tier: 'high',
    label: 'High desktop',
    maxDevicePixelRatio: 1.75,
    postProcessingScale: 0.92,
    bloomStrengthScale: 0.88,
    primaryShadowMapSize: 1536,
    reflections: 'full',
  }),
  ultra: Object.freeze({
    tier: 'ultra',
    label: 'Ultra desktop',
    maxDevicePixelRatio: 2,
    postProcessingScale: 1,
    bloomStrengthScale: 1,
    primaryShadowMapSize: 2048,
    reflections: 'full',
  }),
});

const EMPTY_FRAME_STATS: FrameTimeStatistics = Object.freeze({
  samples: 0,
  windowSeconds: 0,
  averageMs: 0,
  minimumMs: 0,
  maximumMs: 0,
  p50Ms: 0,
  p95Ms: 0,
  p99Ms: 0,
  averageFps: 0,
  onePercentLowFps: 0,
  hitchesOver33Ms: 0,
  hitchesOver50Ms: 0,
});

/**
 * Samples production-facing desktop performance and recommends visual-only
 * quality changes. It never drops coins, bodies, fixed steps, or game rules.
 */
export class DesktopPerformanceGovernor {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene: THREE.Object3D | undefined;
  private readonly physicsSource: (() => PhysicsPerformanceSample) | undefined;
  private readonly applyQualityHook: ((event: QualityChangeEvent) => void) | undefined;
  private readonly listeners = new Set<QualityChangeListener>();
  private readonly frameTimes: Float32Array;
  private readonly warmupSeconds: number;
  private readonly evaluationIntervalSeconds: number;
  private readonly downgradeAfterWindows: number;
  private readonly upgradeAfterWindows: number;
  private readonly tierChangeCooldownSeconds: number;

  private frameWriteIndex = 0;
  private frameSampleCount = 0;
  private elapsedSecondsValue = 0;
  private evaluationAccumulator = 0;
  private currentTierValue: DesktopQualityTier;
  private adaptiveQualityValue: boolean;
  private pressuredWindowsValue = 0;
  private goodWindowsValue = 0;
  private tierChangesValue = 0;
  private lastTierChangeAtSeconds = Number.NEGATIVE_INFINITY;
  private frameStartMilliseconds: number | null = null;
  private lastMeasuredCpuMilliseconds: number | null = null;
  private lastSceneScanAtSeconds = Number.NEGATIVE_INFINITY;
  private sceneSnapshot: ScenePerformanceSnapshot | null = null;
  private recommendation: DesktopQualityRecommendation = Object.freeze({
    tier: 'high',
    bottleneck: 'warming-up',
    reason: 'Collecting stable frame samples.',
    confidence: 'low',
  });
  private disposed = false;

  constructor(options: PerformanceGovernorOptions) {
    this.renderer = options.renderer;
    this.scene = options.scene;
    this.physicsSource = options.physics;
    this.applyQualityHook = options.applyQuality;
    this.currentTierValue = options.initialTier ?? 'high';
    this.adaptiveQualityValue = options.adaptiveQuality ?? true;
    this.warmupSeconds = readPositive(options.warmupSeconds ?? 4, 'warmupSeconds');
    this.evaluationIntervalSeconds = readPositive(
      options.evaluationIntervalSeconds ?? 2,
      'evaluationIntervalSeconds',
    );
    this.downgradeAfterWindows = readPositiveInteger(
      options.downgradeAfterWindows ?? 3,
      'downgradeAfterWindows',
    );
    this.upgradeAfterWindows = readPositiveInteger(
      options.upgradeAfterWindows ?? 8,
      'upgradeAfterWindows',
    );
    this.tierChangeCooldownSeconds = readPositive(
      options.tierChangeCooldownSeconds ?? 10,
      'tierChangeCooldownSeconds',
    );
    this.frameTimes = new Float32Array(
      readIntegerInRange(options.sampleCapacity ?? 240, 'sampleCapacity', 60, 1200),
    );
    this.recommendation = Object.freeze({
      ...this.recommendation,
      tier: this.currentTierValue,
    });
  }

  get currentTier(): DesktopQualityTier {
    return this.currentTierValue;
  }

  get currentProfile(): DesktopQualityProfile {
    return DESKTOP_QUALITY_PROFILES[this.currentTierValue];
  }

  get adaptiveQualityEnabled(): boolean {
    return this.adaptiveQualityValue;
  }

  /** Use this at the beginning of the game's single animation-frame callback. */
  beginFrame(nowMilliseconds = readClockMilliseconds()): void {
    this.assertActive();
    this.frameStartMilliseconds = nowMilliseconds;
  }

  /**
   * Call after rendering. `deltaSeconds` should be the real frame interval;
   * fixed-step physics remains owned by MedalPhysicsSystem.
   */
  endFrame(
    deltaSeconds: number,
    options: RecordFrameOptions = {},
    nowMilliseconds = readClockMilliseconds(),
  ): void {
    this.assertActive();
    const delta = readNonNegative(deltaSeconds, 'deltaSeconds');
    if (this.frameStartMilliseconds !== null) {
      this.lastMeasuredCpuMilliseconds = Math.max(0, nowMilliseconds - this.frameStartMilliseconds);
    }
    this.frameStartMilliseconds = null;

    if (options.excluded || delta === 0 || delta > 0.25) return;
    this.elapsedSecondsValue += delta;
    this.evaluationAccumulator += delta;
    this.pushFrameTime(delta * 1000);

    if (
      this.elapsedSecondsValue >= this.warmupSeconds &&
      this.evaluationAccumulator >= this.evaluationIntervalSeconds
    ) {
      this.evaluationAccumulator %= this.evaluationIntervalSeconds;
      this.evaluateQuality();
    }
  }

  /** Records a frame without CPU-phase timing. */
  recordFrame(deltaSeconds: number, options: RecordFrameOptions = {}): void {
    this.endFrame(deltaSeconds, options);
  }

  applyInitialQuality(): void {
    this.assertActive();
    this.emitTierChange(this.currentTierValue, this.currentTierValue, 'initial');
  }

  setAdaptiveQuality(enabled: boolean): void {
    this.assertActive();
    this.adaptiveQualityValue = enabled;
    this.goodWindowsValue = 0;
    this.pressuredWindowsValue = 0;
  }

  setTier(tier: DesktopQualityTier): void {
    this.assertActive();
    assertTier(tier);
    this.changeTier(tier, 'manual');
  }

  subscribe(listener: QualityChangeListener): () => void {
    this.assertActive();
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Forces the more expensive scene traversal; intended for QA snapshots. */
  refreshSceneDiagnostics(): ScenePerformanceSnapshot | null {
    this.assertActive();
    if (!this.scene) return null;
    this.sceneSnapshot = collectSceneDiagnostics(this.scene, this.elapsedSecondsValue);
    this.lastSceneScanAtSeconds = this.elapsedSecondsValue;
    return this.sceneSnapshot;
  }

  getDiagnostics(refreshScene = false): PerformanceGovernorSnapshot {
    this.assertActive();
    if (
      this.scene &&
      (refreshScene || this.elapsedSecondsValue - this.lastSceneScanAtSeconds >= 5)
    ) {
      this.refreshSceneDiagnostics();
    }

    return Object.freeze({
      version: 1 as const,
      mode: 'desktop' as const,
      elapsedSeconds: round(this.elapsedSecondsValue, 3),
      adaptiveQualityEnabled: this.adaptiveQualityValue,
      currentTier: this.currentTierValue,
      currentProfile: this.currentProfile,
      recommendation: this.recommendation,
      frame: this.calculateFrameStatistics(),
      lastMeasuredCpuMs:
        this.lastMeasuredCpuMilliseconds === null
          ? null
          : round(this.lastMeasuredCpuMilliseconds, 3),
      renderer: collectRendererDiagnostics(this.renderer),
      physics: this.readPhysicsDiagnostics(),
      scene: this.sceneSnapshot,
      adaptation: Object.freeze({
        warmupComplete: this.elapsedSecondsValue >= this.warmupSeconds,
        goodWindows: this.goodWindowsValue,
        pressuredWindows: this.pressuredWindowsValue,
        secondsSinceTierChange: Number.isFinite(this.lastTierChangeAtSeconds)
          ? round(this.elapsedSecondsValue - this.lastTierChangeAtSeconds, 3)
          : 0,
        tierChanges: this.tierChangesValue,
      }),
    });
  }

  /** A serializable bridge that Playwright can inspect without private access. */
  createDiagnosticsBridge(): PerformanceDiagnosticsBridge {
    this.assertActive();
    const governor = this;
    return Object.freeze({
      get snapshot() {
        return governor.getDiagnostics();
      },
      refresh: () => governor.getDiagnostics(true),
      setAdaptiveQuality: (enabled: boolean) => {
        governor.setAdaptiveQuality(Boolean(enabled));
        return governor.getDiagnostics();
      },
      setTier: (tier: DesktopQualityTier) => {
        governor.setTier(tier);
        return governor.getDiagnostics();
      },
    });
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.listeners.clear();
    this.frameStartMilliseconds = null;
  }

  private pushFrameTime(milliseconds: number): void {
    this.frameTimes[this.frameWriteIndex] = milliseconds;
    this.frameWriteIndex = (this.frameWriteIndex + 1) % this.frameTimes.length;
    this.frameSampleCount = Math.min(this.frameSampleCount + 1, this.frameTimes.length);
  }

  private evaluateQuality(): void {
    const statistics = this.calculateFrameStatistics();
    const physics = this.readPhysicsDiagnostics();
    const renderer = collectRendererDiagnostics(this.renderer);
    this.recommendation = recommendQuality(statistics, renderer, physics, this.currentTierValue);

    if (!this.adaptiveQualityValue || statistics.samples < 60) return;
    if (this.elapsedSecondsValue - this.lastTierChangeAtSeconds < this.tierChangeCooldownSeconds) {
      this.goodWindowsValue = 0;
      this.pressuredWindowsValue = 0;
      return;
    }

    const currentIndex = QUALITY_ORDER.indexOf(this.currentTierValue);
    const recommendedIndex = QUALITY_ORDER.indexOf(this.recommendation.tier);
    if (recommendedIndex < currentIndex) {
      this.pressuredWindowsValue += 1;
      this.goodWindowsValue = 0;
      if (this.pressuredWindowsValue >= this.downgradeAfterWindows) {
        this.changeTier(QUALITY_ORDER[currentIndex - 1] ?? 'safe', 'sustained-pressure');
      }
      return;
    }

    if (recommendedIndex > currentIndex) {
      this.goodWindowsValue += 1;
      this.pressuredWindowsValue = 0;
      if (this.goodWindowsValue >= this.upgradeAfterWindows) {
        this.changeTier(QUALITY_ORDER[currentIndex + 1] ?? 'ultra', 'sustained-headroom');
      }
      return;
    }

    this.goodWindowsValue = 0;
    this.pressuredWindowsValue = 0;
  }

  private changeTier(
    tier: DesktopQualityTier,
    reason: Exclude<QualityChangeEvent['reason'], 'initial'>,
  ): void {
    if (tier === this.currentTierValue && reason !== 'manual') return;
    const previous = this.currentTierValue;
    this.currentTierValue = tier;
    this.lastTierChangeAtSeconds = this.elapsedSecondsValue;
    this.goodWindowsValue = 0;
    this.pressuredWindowsValue = 0;
    if (previous !== tier) this.tierChangesValue += 1;
    this.emitTierChange(previous, tier, reason);
  }

  private emitTierChange(
    previousTier: DesktopQualityTier,
    nextTier: DesktopQualityTier,
    reason: QualityChangeEvent['reason'],
  ): void {
    const event = Object.freeze({
      previousTier,
      nextTier,
      profile: DESKTOP_QUALITY_PROFILES[nextTier],
      reason,
      elapsedSeconds: round(this.elapsedSecondsValue, 3),
    });
    this.applyQualityHook?.(event);
    for (const listener of this.listeners) listener(event);
  }

  private calculateFrameStatistics(): FrameTimeStatistics {
    if (this.frameSampleCount === 0) return EMPTY_FRAME_STATS;
    const values = new Array<number>(this.frameSampleCount);
    let total = 0;
    let minimum = Number.POSITIVE_INFINITY;
    let maximum = 0;
    let hitchesOver33 = 0;
    let hitchesOver50 = 0;
    for (let index = 0; index < this.frameSampleCount; index += 1) {
      const value = this.frameTimes[index] ?? 0;
      values[index] = value;
      total += value;
      minimum = Math.min(minimum, value);
      maximum = Math.max(maximum, value);
      if (value > 33.333) hitchesOver33 += 1;
      if (value > 50) hitchesOver50 += 1;
    }
    values.sort((a, b) => a - b);
    const average = total / this.frameSampleCount;
    const p99 = percentile(values, 0.99);
    return Object.freeze({
      samples: this.frameSampleCount,
      windowSeconds: round(total / 1000, 3),
      averageMs: round(average, 3),
      minimumMs: round(minimum, 3),
      maximumMs: round(maximum, 3),
      p50Ms: round(percentile(values, 0.5), 3),
      p95Ms: round(percentile(values, 0.95), 3),
      p99Ms: round(p99, 3),
      averageFps: round(1000 / Math.max(average, 0.001), 2),
      onePercentLowFps: round(1000 / Math.max(p99, 0.001), 2),
      hitchesOver33Ms: hitchesOver33,
      hitchesOver50Ms: hitchesOver50,
    });
  }

  private readPhysicsDiagnostics(): PhysicsPerformanceSample | null {
    if (!this.physicsSource) return null;
    const sample = this.physicsSource();
    return Object.freeze({
      engine: sample.engine,
      activeItems: readCount(sample.activeItems, 'physics.activeItems'),
      activeCoins: readCount(sample.activeCoins, 'physics.activeCoins'),
      activeCards: readCount(sample.activeCards, 'physics.activeCards'),
      sleepingItems: readCount(sample.sleepingItems, 'physics.sleepingItems'),
      bodies: readCount(sample.bodies, 'physics.bodies'),
      colliders: readCount(sample.colliders, 'physics.colliders'),
      ccdBodies: readCount(sample.ccdBodies, 'physics.ccdBodies'),
      fixedTimeStepSeconds: readPositive(
        sample.fixedTimeStepSeconds,
        'physics.fixedTimeStepSeconds',
      ),
      lastFrameFixedSteps: readCount(
        sample.lastFrameFixedSteps,
        'physics.lastFrameFixedSteps',
      ),
      ...(sample.droppedAccumulatorSeconds === undefined
        ? {}
        : {
            droppedAccumulatorSeconds: readNonNegative(
              sample.droppedAccumulatorSeconds,
              'physics.droppedAccumulatorSeconds',
            ),
          }),
      ...(sample.estimatedCoinDrawCalls === undefined
        ? {}
        : {
            estimatedCoinDrawCalls: readCount(
              sample.estimatedCoinDrawCalls,
              'physics.estimatedCoinDrawCalls',
            ),
          }),
    });
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('DesktopPerformanceGovernor is disposed.');
  }
}

export function installPerformanceDiagnosticsBridge(
  governor: DesktopPerformanceGovernor,
  target: Window & typeof globalThis = window,
  key = '__COIN_PUSHER_PERFORMANCE__',
): () => void {
  const record = target as unknown as Record<string, unknown>;
  const bridge = governor.createDiagnosticsBridge();
  record[key] = bridge;
  return () => {
    if (record[key] === bridge) delete record[key];
  };
}

function recommendQuality(
  frame: FrameTimeStatistics,
  renderer: RendererPerformanceSnapshot,
  physics: PhysicsPerformanceSample | null,
  currentTier: DesktopQualityTier,
): DesktopQualityRecommendation {
  if (frame.samples < 60) {
    return Object.freeze({
      tier: currentTier,
      bottleneck: 'warming-up',
      reason: `Collecting stable frame samples (${frame.samples}/60).`,
      confidence: 'low',
    });
  }

  const pressure = frame.p95Ms > 21 || frame.averageFps < 50;
  const severePressure = frame.p95Ms > 29 || frame.averageFps < 38;
  const drawPressure = renderer.calls > 260 || renderer.triangles > 1_750_000;
  const fillPressure = renderer.pixelRatio > 1.5 && renderer.drawingBufferWidth * renderer.drawingBufferHeight > 3_500_000;
  const physicsPressure = Boolean(
    physics &&
      (physics.lastFrameFixedSteps >= 4 ||
        (physics.droppedAccumulatorSeconds ?? 0) > 0.05 ||
        physics.activeItems > 170),
  );

  let bottleneck: PerformanceBottleneck = 'none';
  if (pressure) {
    if (physicsPressure && (drawPressure || fillPressure)) bottleneck = 'mixed';
    else if (physicsPressure) bottleneck = 'cpu-or-physics';
    else if (fillPressure) bottleneck = 'gpu-fill';
    else if (drawPressure) bottleneck = 'gpu-draw';
    else bottleneck = 'mixed';
  }

  let tier: DesktopQualityTier;
  if (severePressure) tier = 'safe';
  else if (frame.p95Ms > 23.5 || frame.averageFps < 44) tier = 'balanced';
  else if (pressure) tier = 'high';
  else if (frame.p95Ms <= 18.2 && frame.averageFps >= 56 && frame.onePercentLowFps >= 48) tier = 'ultra';
  else tier = 'high';

  const reason = pressure
    ? `${describeBottleneck(bottleneck)} Frame p95 is ${frame.p95Ms.toFixed(1)} ms at ${frame.averageFps.toFixed(1)} average FPS.`
    : `Stable desktop headroom: ${frame.p95Ms.toFixed(1)} ms p95 at ${frame.averageFps.toFixed(1)} average FPS.`;

  return Object.freeze({
    tier,
    bottleneck,
    reason,
    confidence: frame.samples >= 180 ? 'high' : 'medium',
  });
}

function describeBottleneck(bottleneck: PerformanceBottleneck): string {
  if (bottleneck === 'gpu-fill') return 'Likely fill-rate pressure from DPR, glass, or post-processing.';
  if (bottleneck === 'gpu-draw') return 'Likely draw or triangle pressure.';
  if (bottleneck === 'cpu-or-physics') return 'Likely CPU or fixed-step physics pressure.';
  if (bottleneck === 'mixed') return 'Mixed render/simulation pressure.';
  return '';
}

function collectRendererDiagnostics(renderer: THREE.WebGLRenderer): RendererPerformanceSnapshot {
  const size = renderer.getDrawingBufferSize(new THREE.Vector2());
  const info = renderer.info;
  return Object.freeze({
    calls: info.render.calls,
    triangles: info.render.triangles,
    points: info.render.points,
    lines: info.render.lines,
    geometries: info.memory.geometries,
    textures: info.memory.textures,
    programs: info.programs?.length ?? 0,
    pixelRatio: round(renderer.getPixelRatio(), 3),
    drawingBufferWidth: size.x,
    drawingBufferHeight: size.y,
    shadowMapsEnabled: renderer.shadowMap.enabled,
  });
}

function collectSceneDiagnostics(
  root: THREE.Object3D,
  elapsedSeconds: number,
): ScenePerformanceSnapshot {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  const textures = new Set<THREE.Texture>();
  let objects = 0;
  let visibleObjects = 0;
  let meshes = 0;
  let instancedMeshes = 0;
  let instances = 0;
  let lights = 0;
  let shadowCasters = 0;
  let shadowReceivers = 0;
  let transparentMeshes = 0;
  let approximateTriangles = 0;
  let approximateDrawSurfaces = 0;

  root.traverse((object) => {
    objects += 1;
    if (object.visible) visibleObjects += 1;
    if ((object as THREE.Light).isLight) lights += 1;
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    meshes += 1;
    if (mesh.castShadow) shadowCasters += 1;
    if (mesh.receiveShadow) shadowReceivers += 1;
    geometries.add(mesh.geometry);

    const instanced = object as THREE.InstancedMesh;
    const instanceMultiplier = instanced.isInstancedMesh ? instanced.count : 1;
    if (instanced.isInstancedMesh) {
      instancedMeshes += 1;
      instances += instanced.count;
    }
    const position = mesh.geometry.getAttribute('position');
    const baseTriangles = mesh.geometry.index
      ? mesh.geometry.index.count / 3
      : (position?.count ?? 0) / 3;
    approximateTriangles += Math.round(baseTriangles * instanceMultiplier);

    const meshMaterials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    approximateDrawSurfaces += Math.max(1, meshMaterials.length);
    if (meshMaterials.some((material) => material.transparent)) transparentMeshes += 1;
    for (const material of meshMaterials) {
      materials.add(material);
      for (const value of Object.values(material as unknown as Record<string, unknown>)) {
        if (value && typeof value === 'object' && (value as THREE.Texture).isTexture) {
          textures.add(value as THREE.Texture);
        }
      }
    }
  });

  return Object.freeze({
    scannedAtSeconds: round(elapsedSeconds, 3),
    objects,
    visibleObjects,
    meshes,
    instancedMeshes,
    instances,
    lights,
    shadowCasters,
    shadowReceivers,
    transparentMeshes,
    geometries: geometries.size,
    materials: materials.size,
    textures: textures.size,
    approximateTriangles,
    approximateDrawSurfaces,
  });
}

function percentile(sortedValues: readonly number[], fraction: number): number {
  if (sortedValues.length === 0) return 0;
  const index = Math.min(sortedValues.length - 1, Math.ceil(sortedValues.length * fraction) - 1);
  return sortedValues[Math.max(0, index)] ?? 0;
}

function assertTier(tier: string): asserts tier is DesktopQualityTier {
  if (!QUALITY_ORDER.includes(tier as DesktopQualityTier)) {
    throw new RangeError(`Unknown desktop quality tier: ${tier}`);
  }
}

function readClockMilliseconds(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

function readNonNegative(value: number, label: string): number {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${label} must be a finite number at least zero.`);
  }
  return value;
}

function readPositive(value: number, label: string): number {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${label} must be a finite number greater than zero.`);
  }
  return value;
}

function readPositiveInteger(value: number, label: string): number {
  if (!Number.isInteger(value) || value <= 0) {
    throw new RangeError(`${label} must be a positive integer.`);
  }
  return value;
}

function readIntegerInRange(value: number, label: string, minimum: number, maximum: number): number {
  if (!Number.isInteger(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be an integer from ${minimum} to ${maximum}.`);
  }
  return value;
}

function readCount(value: number, label: string): number {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${label} must be a non-negative integer.`);
  }
  return value;
}

function round(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}
